/**
 * useMembersTab
 *
 * Encapsulates all Members tab data + actions (fetch members, fetch invites,
 * invite/update/remove/resend/cancel). Pure data layer — the page renders.
 *
 * Bearer-only Central calls via services/membershipApi.
 */

import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type {
  CentralRole,
  PendingInvite,
  WorkspaceMember,
} from '@levelup/central-auth';

import { selectCentralAccessToken } from '../store/slices/centralAuthSlice';
import {
  BEHEARD_INVITE_ROLE_OPTIONS,
  cancelInvite,
  fetchInviteOptions,
  inviteMember,
  listMembers,
  listPendingInvites,
  removeMember,
  resendInvite,
  updateMemberRole,
  type InviteRoleOption,
} from '../services/membershipApi';

interface UseMembersTabArgs {
  workspaceId: string;
  canManageMembers: boolean;
}

interface InviteParams {
  email: string;
  role: CentralRole;
  templateId?: string;
  message?: string;
}

export interface MembersTabController {
  members: WorkspaceMember[];
  invites: PendingInvite[];
  isLoadingMembers: boolean;
  isLoadingInvites: boolean;
  membersError: string | null;
  invitesError: string | null;
  inviteSubmitting: boolean;
  inviteError: string | null;
  inviteModalOpen: boolean;
  setInviteModalOpen: (open: boolean) => void;
  handleInvite: (params: InviteParams) => Promise<void>;
  handleUpdateRole: (userId: string, newRole: CentralRole) => Promise<void>;
  handleRemoveMember: (userId: string) => Promise<void>;
  handleResendInvite: (inviteId: string) => Promise<void>;
  handleCancelInvite: (inviteId: string) => Promise<void>;
  /** Rich invite-role options (Central templates + BeHeard local fallback). */
  inviteRoleOptions: InviteRoleOption[];
}

export function useMembersTab(args: UseMembersTabArgs): MembersTabController {
  const { workspaceId, canManageMembers } = args;
  const token = useSelector(selectCentralAccessToken);

  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(true);
  const [membersError, setMembersError] = useState<string | null>(null);

  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [isLoadingInvites, setIsLoadingInvites] = useState(true);
  const [invitesError, setInvitesError] = useState<string | null>(null);

  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  // Initialize with the module-level constant — always available,
  // no async gap. Central fetch may upgrade later if it has richer data.
  const [inviteRoleOptions, setInviteRoleOptions] = useState<InviteRoleOption[]>(
    BEHEARD_INVITE_ROLE_OPTIONS,
  );

  const fetchMembers = useCallback(async () => {
    if (!workspaceId || !token) {
      setIsLoadingMembers(false);
      return;
    }
    setIsLoadingMembers(true);
    setMembersError(null);
    try {
      const result = await listMembers(token, workspaceId);
      setMembers(Array.isArray(result) ? result : []);
    } catch (err) {
      setMembersError(err instanceof Error ? err.message : 'Failed to load members');
      setMembers([]);
    } finally {
      setIsLoadingMembers(false);
    }
  }, [workspaceId, token]);

  const fetchInvites = useCallback(async () => {
    if (!workspaceId || !token || !canManageMembers) {
      setIsLoadingInvites(false);
      return;
    }
    setIsLoadingInvites(true);
    setInvitesError(null);
    try {
      const result = await listPendingInvites(token, workspaceId);
      setInvites(Array.isArray(result) ? result : []);
    } catch (err) {
      setInvitesError(err instanceof Error ? err.message : 'Failed to load invites');
      setInvites([]);
    } finally {
      setIsLoadingInvites(false);
    }
  }, [workspaceId, token, canManageMembers]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);
  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  // Fetch rich invite-role options (Central templates + BeHeard fallback)
  useEffect(() => {
    if (!workspaceId || !token || !canManageMembers) {
      setInviteRoleOptions([]);
      return;
    }
    let cancelled = false;
    fetchInviteOptions(token, workspaceId)
      .then((opts) => {
        if (!cancelled) setInviteRoleOptions(opts);
      })
      .catch(() => {
        if (!cancelled) setInviteRoleOptions([]);
      });
    return () => { cancelled = true; };
  }, [workspaceId, token, canManageMembers]);

  const handleInvite = useCallback(
    async (params: InviteParams) => {
      if (!token) return;
      setInviteSubmitting(true);
      setInviteError(null);
      try {
        await inviteMember(token, workspaceId, params);
        setInviteModalOpen(false);
        fetchInvites();
      } catch (err) {
        setInviteError(err instanceof Error ? err.message : 'Invite failed');
        throw err;
      } finally {
        setInviteSubmitting(false);
      }
    },
    [token, workspaceId, fetchInvites],
  );

  const handleUpdateRole = useCallback(
    async (userId: string, newRole: CentralRole) => {
      if (!token) return;
      await updateMemberRole(token, workspaceId, userId, newRole);
      fetchMembers();
    },
    [token, workspaceId, fetchMembers],
  );

  const handleRemoveMember = useCallback(
    async (userId: string) => {
      if (!token) return;
      await removeMember(token, workspaceId, userId);
      fetchMembers();
    },
    [token, workspaceId, fetchMembers],
  );

  const handleResendInvite = useCallback(
    async (inviteId: string) => {
      if (!token) return;
      await resendInvite(token, workspaceId, inviteId);
    },
    [token, workspaceId],
  );

  const handleCancelInvite = useCallback(
    async (inviteId: string) => {
      if (!token) return;
      await cancelInvite(token, workspaceId, inviteId);
      fetchInvites();
    },
    [token, workspaceId, fetchInvites],
  );

  return {
    members,
    invites,
    isLoadingMembers,
    isLoadingInvites,
    membersError,
    invitesError,
    inviteSubmitting,
    inviteError,
    inviteModalOpen,
    setInviteModalOpen,
    handleInvite,
    handleUpdateRole,
    handleRemoveMember,
    handleResendInvite,
    handleCancelInvite,
    inviteRoleOptions,
  };
}
